// Storage Bridge - Cross-domain storage communication
declare global {
    interface Window {
        storageCallbacks: Record<string, (response: any) => void>;
    }
}
// Wrap everything in an IIFE to avoid global pollution
export default (function () {

    function getTopLevelDomain(url: string): string {
        const domain = new URL(url).hostname;
        const parts = domain.split('.');
        let minParts = 3;
        if (parts[parts.length - 1].length > 2) {
            minParts = 2;
        }
        if (parts.length > minParts) {
            return parts.slice(-minParts).join('.');
        }
        return domain;
    }
    function getCurrentTLD(): string {
        try {
            return getTopLevelDomain(window.location.href);
        } catch (e) {
            return "";
        }
    }
    const currentTLD = getCurrentTLD();

    // Generate unique sender ID for this bridge instance
    const BRIDGE_SENDER_ID = 'bridge_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();

    // Simple origin caching
    const CACHE_KEY = btoa('_bc_allowed_origins');
    const CACHE_EXPIRY_MS = 1 * 24 * 60 * 60 * 1000; // 1 day

    const isOriginCached = (origin: string): boolean => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return false;

            const origins = JSON.parse(cached);
            const timestamp = origins[origin];

            if (!timestamp) return false;

            // Check if cache entry is still valid
            return (Date.now() - timestamp) < CACHE_EXPIRY_MS;
        } catch (e) {
            return false;
        }
    };

    const saveAllowedOrigin = (origin: string): void => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            const origins = cached ? JSON.parse(cached) : {};

            origins[origin] = Date.now();
            localStorage.setItem(CACHE_KEY, JSON.stringify(origins));
        } catch (e) {
            console.warn('Storage bridge: Failed to cache origin:', e);
        }
    };

    const isOriginAllowed = async (origin: string) => {
        if (currentTLD && getTopLevelDomain(origin) === currentTLD) {
            return true;
        }
        // Check cache first
        if (isOriginCached(origin)) {
            return true;
        }

        try {
            // make an api call to the server to check if the origin is allowed
            const response = await fetch(`${window.location.origin}/api-rewards/config/check?o=${encodeURIComponent(origin)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            const allowed = data?.allowed ?? false;
            if (allowed) {
                saveAllowedOrigin(origin);
            }
            return allowed;
        } catch (e) {
            console.error('Storage bridge: Error checking origin:', e);
            return false;
        }
    };

    // Rate limiting
    const messageRateLimit = new Map();
    const MAX_MESSAGES_PER_MINUTE = 30;
    // Main message handler
    window.addEventListener('message', async (event) => {
        // Security: Only accept messages from allowed origin patterns
        if (!await isOriginAllowed(event.origin)) {
            console.warn('Storage bridge: Rejected message from unauthorized origin:', event.origin);
            return;
        }
        // Rate limiting
        const now = Date.now();
        const minute = Math.floor(now / 60000);
        const key = `${event.origin}_${minute}`;
        const count = messageRateLimit.get(key) || 0;
        if (count >= MAX_MESSAGES_PER_MINUTE) {
            console.warn('Storage bridge: Rate limit exceeded for origin:', event.origin);
            return;
        }
        messageRateLimit.set(key, count + 1);
        // Clean old rate limit entries
        for (const [k] of messageRateLimit.entries()) {
            if (parseInt(k.split('_')[1]) < minute - 5) {
                messageRateLimit.delete(k);
            }
        }
        const message = event.data;
        // Ignore messages from self using sender ID
        if (message?.senderId === BRIDGE_SENDER_ID) {
            return;
        }
        // Validate message structure
        if (!message || typeof message !== 'object' || !message.messageId) {
            return;
        }
        const sendResponse = (response: any) => {
            // Add our sender ID to prevent self-processing
            response.senderId = BRIDGE_SENDER_ID;
            // Same-origin: use direct callback if available
            if (event.origin === window.location.origin) {
                const callback = window.storageCallbacks?.[message.messageId];
                if (callback) {
                    callback(response);
                    delete window.storageCallbacks[message.messageId];
                    return;
                }
            }
            // Cross-origin: use postMessage
            if (event.source) {
                (event.source as Window).postMessage(response, { targetOrigin: event.origin });
            }
        };

        try {
            const response = handleStorageAction(message);
            sendResponse(response);
        } catch (error) {
            sendResponse({
                messageId: message.messageId,
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            });
            console.error('Bridge error:', error);
        }
    });

    window.storageCallbacks = {};

    function handleStorageAction(message: any) {
        const { action, key, value, messageId, stage } = message;
        // Validate inputs
        if (!messageId || typeof messageId !== 'string') {
            throw new Error('Invalid messageId');
        }
        if (!action || typeof action !== 'string') {
            throw new Error('Invalid action');
        }
        // Validate key and value for applicable actions
        if (['setItem', 'removeItem'].includes(action)) {
            if (!key || typeof key !== 'string') {
                throw new Error('Invalid key');
            }
            if (key.length > 1024) {
                throw new Error('Key too long');
            }
            // Prevent prototype pollution
            if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
                throw new Error('Invalid key name');
            }
        }
        if (action === 'setItem') {
            if (typeof value !== 'string') {
                throw new Error('Invalid value type');
            }
            if (value.length > 1024 * 1024) { // 1MB limit
                throw new Error('Value too large');
            }
        }
        let response: {
            messageId: string;
            success: boolean;
            stage: string;
            data?: Record<string, string | null>;
        } = {
            messageId,
            stage,
            success: true
        };

        try {
            switch (action) {
                case 'getAll':
                    // Return all localStorage data for this stage only
                    const stageData: Record<string, string | null> = {};
                    for (let i = 0; i < localStorage.length; i++) {
                        const storageKey = localStorage.key(i);
                        if (storageKey) {
                            stageData[storageKey] = localStorage.getItem(storageKey);
                        }
                    }
                    // console.log('Storage bridge: getAll', stageData);
                    response.data = stageData;
                    break;

                case 'setItem':
                    localStorage.setItem(key, value);
                    break;

                case 'removeItem':
                    localStorage.removeItem(key);
                    break;

                case 'clear':
                    const keysToRemove: string[] = [];
                    for (let i = 0; i < localStorage.length; i++) {
                        const storageKey = localStorage.key(i);
                        if (storageKey) {
                            keysToRemove.push(storageKey);
                        }
                    }
                    keysToRemove.forEach(k => localStorage.removeItem(k));
                    break;

                default:
                    throw new Error(`Unknown action: ${action}`);
            }
        } catch (storageError) {
            // Handle storage quota exceeded or other localStorage errors
            const errorMessage = storageError instanceof Error ? storageError.message : 'Storage operation failed';
            throw new Error(`Storage error: ${errorMessage}`);
        }

        return response;
    }

    // Send ready signal when bridge is loaded
    window.addEventListener('load', () => {
        // Signal to parent that bridge is ready
        if (window.parent !== window) {
            window.parent.postMessage({
                type: 'bridge-ready',
                origin: window.location.origin
            }, '*');
        }
    });

    // Error handling
    window.addEventListener('error', (event) => {
        console.error('Storage bridge error:', event.error);
    });

})(); // End IIFE